// The coding model a box mint names (plan 102 slice 05, decision 3 as revised): the
// `coding_model` field of `POST /v1/runners/enrollments`' 201
// (`apps/api/src/routes/runner-enrollment-mint.ts`, the domain's `BoxModelWire`).
//
//   { id, source: 'request' | 'key_catalog', used_when?, detail? }
//       the join command exports `id` as DZ_MODEL_OVERRIDE. `request` = the caller
//       named it; `key_catalog` = prefilled from the account's verified org keys.
//       `used_when: 'no_verified_key'` = it runs only while the account has no
//       verified key (a run with a key drives that key's model).
//   { id: null, source: null, reason: 'no_verified_key', detail }
//       nothing to prefill: the command prompts for the model on the box.
//
// Which model and where it came from is the page's own `t()` copy, keyed on the
// STRUCTURED fields. `detail` is the server's own sentence (when the model runs at
// all, or why nothing was prefilled) and is shown AS GIVEN, like a refusal's detail:
// one spelling, the domain's (`BOX_MODEL_FLOOR_DETAIL`, `BOX_MODEL_PROMPT_REASON`).
//
// TOLERANT on purpose, unlike the key verify parse: the field rides a mint that
// already burnt a box slot and carries a one-time token. A body this build cannot
// read must not throw that token away, so an absent or unknown `coding_model`
// reads as `null` — the dialog then says nothing about the model and the command
// keeps whatever the person typed (or its own prompt).

import { z } from 'zod';
import { t } from '../i18n';

const CodingModelSchema = z.union([
  z.object({
    id: z.string().min(1),
    source: z.enum(['request', 'key_catalog']),
    detail: z.string().optional(),
  }),
  z.object({
    id: z.null(),
    source: z.null(),
    reason: z.literal('no_verified_key'),
    detail: z.string().optional(),
  }),
]);

export type CodingModel = z.infer<typeof CodingModelSchema>;

/** The mint answer's `coding_model`, or `null` when absent or not a shape this build knows. */
export function parseCodingModel(raw: unknown): CodingModel | null {
  const parsed = CodingModelSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * The sentences a coding model earns. `exported` = the command shown exports the
 * model (Connect's token transport); otherwise the command prompts for it (the
 * onboarding box step) and the line says what to type there.
 */
export function codingModelLines(model: CodingModel, exported: boolean): readonly string[] {
  const detail = model.detail === undefined ? [] : [model.detail];
  if (model.id === null) return detail;
  const vars = { model: model.id };
  // Literal `t()` per arm, so `i18n.coverage.test.ts` sees every key.
  const line = exported
    ? model.source === 'request'
      ? t('fleet.connect.coding_model_request', vars)
      : t('fleet.connect.coding_model_catalog', vars)
    : model.source === 'request'
      ? t('fleet.connect.coding_model_prompt_request', vars)
      : t('fleet.connect.coding_model_prompt_catalog', vars);
  return [line, ...detail];
}
